/**
 * The contract limit shown beside the order quantity (owner 2026-09-28, ADR-0033): "do not cap max
 * positions — only cap it based on the account size" and "there shouldn't be contract limits on
 * HELM either". HELM has **no fixed cap** of its own. Two limits may exist, both from the gateway's
 * `RiskState`:
 *
 * - the user's optional per-account max order quantity (Risk Settings; HELM enforces it at the
 *   gateway, `MAX_QTY`);
 * - the account's Rithmic RMS max order quantity (`ResponseAccountRmsInfo`; Rithmic enforces it
 *   and HELM shows its refusal verbatim).
 *
 * The effective figure is the smaller of the two that are known; none known → no limit shown (and
 * no client-side cap: the quantity stepper is unbounded).
 */

export type QtyLimitFacts = {
  maxOrderQty?: number | undefined;
  rmsMaxOrderQty?: number | undefined;
  rmsBuyLimit?: number | undefined;
  rmsSellLimit?: number | undefined;
  maxPosition?: number | undefined;
};

export type QtyLimit = { max: number; source: "yours" | "rithmic"; title: string };

const known = (v: number | undefined): v is number =>
  typeof v === "number" && Number.isSafeInteger(v) && v > 0;

export function effectiveQtyLimit(r: QtyLimitFacts | undefined | null): QtyLimit | null {
  if (!r) return null;
  const mine = known(r.maxOrderQty) ? r.maxOrderQty : null;
  const rms = known(r.rmsMaxOrderQty) ? r.rmsMaxOrderQty : null;
  if (mine === null && rms === null) return null;
  const parts: string[] = [];
  if (rms !== null) parts.push(`Rithmic (account RMS): max ${String(rms)} per order — Rithmic refuses more`);
  if (known(r.rmsBuyLimit) || known(r.rmsSellLimit)) {
    parts.push(
      `Rithmic position limits: long ${known(r.rmsBuyLimit) ? String(r.rmsBuyLimit) : "—"} / short ${known(r.rmsSellLimit) ? String(r.rmsSellLimit) : "—"}`,
    );
  }
  if (mine !== null)
    parts.push(`Your Risk Settings: max ${String(mine)} per order (TradePrizm refuses more)`);
  if (known(r.maxPosition)) parts.push(`Your Risk Settings: max position ${String(r.maxPosition)}`);
  const title = parts.join("\n");
  if (mine !== null && (rms === null || mine <= rms)) return { max: mine, source: "yours", title };
  return { max: rms ?? 0, source: "rithmic", title };
}

/**
 * ⚙ OB4 / owner decision D5 (pending sign-off): with no per-account setting and no max order
 * quantity known, an order above this many contracts asks for confirmation even with one-click on.
 * Change it here; per account, Risk Settings → "Confirm orders above" overrides it.
 */
export const DEFAULT_CONFIRM_QTY_ABOVE = 2;

export type LargeQtyFacts = QtyLimitFacts & { confirmQtyAbove?: number | undefined };

/**
 * The largest quantity that goes out without the large-order confirmation (SPEC §9.8 "orders over
 * 50 % of the max quantity always confirm", Q-L2): the account's own setting when set; else half
 * the effective max order quantity (the user's or Rithmic RMS', rounded down, so 35 → 17 and 18
 * confirms); else {@link DEFAULT_CONFIRM_QTY_ABOVE}. Never a cap: a larger order can still be sent.
 */
export function largeQtyThreshold(r: LargeQtyFacts | undefined | null): number {
  if (r && known(r.confirmQtyAbove)) return r.confirmQtyAbove;
  const lim = effectiveQtyLimit(r);
  if (lim !== null) return Math.floor(lim.max / 2);
  return DEFAULT_CONFIRM_QTY_ABOVE;
}

/** Does this order need the large-quantity confirmation (always, one-click or not)? */
export function needsLargeQtyConfirm(qty: number, r: LargeQtyFacts | undefined | null): boolean {
  return qty > largeQtyThreshold(r);
}

/**
 * Must this new order be confirmed before it is sent? With one-click off, always (SPEC §9.8);
 * with one-click on, still whenever the quantity is large (`large`: show {@link largeQtyWords}) —
 * unless the user turned large-order confirmation off (`confirmLarge` false: Settings → "Confirm
 * large orders", or "Don't ask again" on the popover; owner 2026-09-30, Q-L2). Then one-click
 * sends at once and one-click off shows the ordinary confirmation, exactly as before OB4.
 */
export function orderConfirmation(
  oneClick: boolean,
  qty: number,
  r: LargeQtyFacts | undefined | null,
  confirmLarge: boolean,
): { confirm: boolean; large: boolean; threshold: number } {
  const threshold = largeQtyThreshold(r);
  const large = confirmLarge && qty > threshold;
  return { confirm: large || !oneClick, large, threshold };
}

/**
 * The synced user setting (`settings/main` → `confirm_large_orders`, per user, every device):
 * on unless it is explicitly `false` (missing or anything else = on, the safe default).
 */
export function confirmLargeOrdersOf(v: unknown): boolean {
  return v !== false;
}

/** What the confirm popover answered: confirmed or not, and whether "Don't ask again" was ticked. */
export type ConfirmChoice = { ok: boolean; dontAskAgain: boolean };

/**
 * The settings patch a popover answer saves: only a **confirmed** large-order popover with
 * "Don't ask again" ticked turns large-order confirmation off. Cancelling with the box ticked, or
 * ticking it on an ordinary (one-click off) confirmation, saves nothing.
 */
export function largeConfirmPatch(
  large: boolean,
  choice: ConfirmChoice,
): { confirm_large_orders: false } | null {
  return large && choice.ok && choice.dontAskAgain ? { confirm_large_orders: false } : null;
}

/**
 * The whole new-order confirmation step (placeOrder): decide, ask when needed, save the
 * "Don't ask again" choice, and say whether the order may go. `ask` shows the popover (`large`:
 * the amber large-order one, with the "Don't ask again" box); `save` persists a settings patch.
 */
export async function confirmNewOrder(a: {
  oneClick: boolean;
  confirmLarge: boolean;
  qty: number;
  risk: LargeQtyFacts | undefined | null;
  words: string;
  largeWords: (threshold: number) => string;
  ask: (req: { words: string; large: boolean }) => Promise<ConfirmChoice>;
  save: (patch: { confirm_large_orders: false }) => void;
}): Promise<{ asked: boolean; large: boolean; ok: boolean }> {
  const c = orderConfirmation(a.oneClick, a.qty, a.risk, a.confirmLarge);
  if (!c.confirm) return { asked: false, large: false, ok: true };
  const choice = await a.ask(
    c.large ? { words: a.largeWords(c.threshold), large: true } : { words: a.words, large: false },
  );
  const patch = largeConfirmPatch(c.large, choice);
  if (patch !== null) a.save(patch);
  return { asked: true, large: c.large, ok: choice.ok };
}

/** The large-quantity confirmation in words: quantity, side, contract and account, always. */
export function largeQtyWords(
  o: { side: "buy" | "sell"; qty: number; contract: string; accountId: string },
  threshold: number,
): string {
  return `LARGE ORDER: ${o.side === "buy" ? "BUY" : "SELL"} ${String(o.qty)} ${o.contract} on account ${o.accountId} (more than ${String(threshold)} contracts)`;
}
